import { useEffect, useRef, useState } from "react";
import Styles from "./RFPSelectionModal.module.css";
import { IconButton, Modal, Tooltip } from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  endTenderApi,
  getequisitionsList,
} from "../../../Services/ReverseAuction/GetApi";
import { formatDateType } from "../../../commons/FormatDate";
import CircularProgress from "../../ReverseAuction/Bidding/CircularProgress";
import { usePdfPreview } from "../../../hooks/usePdfPreview";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";
import {
  handleRaFinalizeT_C,
  updateQuantityRequest,
  updateWinnerBidStatus,
} from "../../../Services/ReverseAuction/AlterApi";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import { useDispatch } from "react-redux";
import { activeType2Loading } from "../../../Redux/Slices/notificationSlice";
import { activeTenderId, auctionGet } from "../../../Redux/Slices/auctionSlice";
import { useLocation, useNavigate } from "react-router-dom";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

const RFPSelectionModal = ({ open, handleClose, tenderData }) => {
  const isCloseDatePassed =
    tenderData?.originalTenderCloseDateTime &&
    new Date(tenderData.originalTenderCloseDateTime) < new Date();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const url = location?.pathname || "";
  const isSelectionState = url === "/rfq" && tenderData?.auctionType === "OPEN";
  const { handlePreviewGeneralTerms, PdfPreviewModal } = usePdfPreview();

  const lsKey = `rfp_bid_selection_${tenderData?.id}`;

  const [expanded, setExpanded] = useState(0);
  const [reqList, setReqList] = useState([]);
  const [activeReq, setActiveReq] = useState(null);

  const scrollAreaRef = useRef(null);
  const tabRefs = useRef([]);

  const [selectedBids, setSelectedBids] = useState(() => {
    try {
      const stored = localStorage.getItem(lsKey);
      return stored ? JSON.parse(stored).selectedBids || [] : [];
    } catch {
      return [];
    }
  });
  const [rejectedBids, setRejectedBids] = useState(() => {
    try {
      const stored = localStorage.getItem(lsKey);
      return stored ? JSON.parse(stored).rejectedBids || [] : [];
    } catch {
      return [];
    }
  });

  const [pendingBids, setPendingBids] = useState(null);

  const [confirmation, setConfirmation] = useState({
    status: false,
    header: null,
    data: null,
    note: null,
  });

  const [rejectConfirmation, setRejectConfirmation] = useState({
    status: false,
    header: null,
    data: null,
  });

  const [updateQtyConfirmation, setUpdateQtyConfirmation] = useState({
    status: false,
    bidId: null,
    minQty: null,
    index: null,
  });

  // Track which bid's qty input is open { bidId, qty }
  const [editingAlloc, setEditingAlloc] = useState(null);

  useEffect(() => {
    fetchReqList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPendingBids(
      activeReq?.bids?.filter((bid) => bid?.winnerBidStatus === "PENDING"),
    );
  }, [activeReq]);

  useEffect(() => {
    localStorage.setItem(lsKey, JSON.stringify({ selectedBids, rejectedBids }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBids, rejectedBids]);

  useEffect(() => {
    const tab = tabRefs.current[expanded];
    const container = scrollAreaRef.current;
    if (!tab || !container) return;
    const tabLeft = tab.offsetLeft;
    const tabWidth = tab.offsetWidth;
    const containerWidth = container.offsetWidth;
    container.scrollTo({
      left: tabLeft - containerWidth / 2 + tabWidth / 2,
      behavior: "smooth",
    });
  }, [expanded]);

  const fetchReqList = async () => {
    const res = await getequisitionsList(tenderData?.id, "CB");
    if (res?.data?.code === 200) {
      setReqList(res?.data?.data);
      setActiveReq(res?.data?.data?.[expanded]);
      try {
        const stored = localStorage.getItem(lsKey);
        if (stored) {
          const { selectedBids: storedSelected, rejectedBids: storedRejected } =
            JSON.parse(stored);
          if (storedSelected?.length > 0 || storedRejected?.length > 0) {
            setSelectedBids(storedSelected || []);
            setRejectedBids(storedRejected || []);
          }
        }
      } catch {
        // ignore parse errors
      }
    }
  };

  // ─── Derived from local selection state ───────────────────────────────────

  // Sum of allocated qty for locally accepted bids in a requisition
  const getFilledQty = (ele) =>
    (ele?.bids || [])
      .filter((bid) => selectedBids.includes(bid?.id))
      .reduce(
        (sum, bid) =>
          sum + Number(bid?.requestedQuantity || bid?.selectedQuantity || 0),
        0,
      );

  const hasAnyAccepted = selectedBids.length > 0;

  const allBids = reqList.flatMap((item) => item.bids || []);
  const allReqFirstBidsAbsconded =
    reqList.length > 0 &&
    reqList.every((req) => {
      const sortedBids = [...(req?.bids || [])].sort(
        (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
      );
      return sortedBids[0]?.winnerBidStatus === "ABSCOND";
    });
  const allBidsRejected =
    allBids.length > 0 &&
    ((!hasAnyAccepted &&
      rejectedBids.length > 0 &&
      allBids.every((bid) => rejectedBids.includes(bid?.id))) ||
      allBids.every((bid) => bid?.winnerBidStatus === "ABSCOND") ||
      allReqFirstBidsAbsconded);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const onToggle = (index) => {
    setExpanded(index);
    setActiveReq(reqList?.[index]);
  };

  const handleSelectBid = async (id, index) => {
    if (index !== 0) {
      const sortedBids = [...(activeReq?.bids ?? [])].sort(
        (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
      );
      const zerothBid = sortedBids[0];
      if (zerothBid) {
        const zerothHasStatus = selectedBids.includes(zerothBid.id);
        if (zerothHasStatus) {
          setSelectedBids((prev) => (prev.includes(id) ? prev : [...prev, id]));
          setRejectedBids((prev) => prev.filter((bidId) => bidId !== id));
          await handleStatusToggle(id, "ACCEPTED");
          fetchReqList();
        } else {
          ErrorMessage(
            "Please accept the first bid to accept this bid as per tender rules",
          );
          return;
        }
      }
    } else {
      setSelectedBids((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setRejectedBids((prev) => prev.filter((bidId) => bidId !== id));
      await handleStatusToggle(id, "ACCEPTED");
      fetchReqList();
    }
  };

  const handleRejectReqAllBid = async () => {
    const allBidIds = (activeReq?.bids || []).map((bid) => bid.id);
    const bidIdToStatusMap = allBidIds.reduce((acc, id) => {
      acc[id] = "ABSCOND";
      return acc;
    }, {});
    setRejectedBids((prev) => [...new Set([...prev, ...allBidIds])]);
    setSelectedBids((prev) => prev.filter((id) => !allBidIds.includes(id)));
    await updateWinnerBidStatus({ bidIdToStatusMap });
    fetchReqList();
  };

  const handleClear = () => {
    const activeReqBidIds = (activeReq?.bids || []).map((bid) => bid.id);
    setSelectedBids((prev) =>
      prev.filter((id) => !activeReqBidIds.includes(id)),
    );
    setRejectedBids((prev) =>
      prev.filter((id) => !activeReqBidIds.includes(id)),
    );
  };

  const handleRejectBid = async (id, index) => {
    if (index === 0) {
      handleRejectReqAllBid();
    } else {
      const sortedBids = [...(activeReq?.bids ?? [])].sort(
        (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
      );
      const zerothBid = sortedBids[0];
      if (zerothBid) {
        const zerothHasStatus =
          selectedBids.includes(zerothBid.id) ||
          rejectedBids.includes(zerothBid.id);
        if (zerothHasStatus) {
          setRejectedBids((prev) => (prev.includes(id) ? prev : [...prev, id]));
          setSelectedBids((prev) => prev.filter((bidId) => bidId !== id));
          await handleStatusToggle(id, "ABSCOND");
        } else {
          ErrorMessage(
            "Please reject the first bid to reject this bid as per tender rules",
          );
          return;
        }
      }

      fetchReqList();
    }
  };

  const handleStatusToggle = async (bidId, newStatus) => {
    const payload = { bidIdToStatusMap: { [bidId]: newStatus } };
    await updateWinnerBidStatus(payload);
  };

  const openEditAlloc = (bid) => {
    setEditingAlloc({
      bidId: bid?.id,
      qty: bid?.requestedQuantity || bid?.selectedQuantity || "",
    });
  };

  const closeEditAlloc = () => setEditingAlloc(null);

  const handleUpdateQuantityRequest = async (
    bidId,
    minQty,
    index,
    minBidQuantityMw,
  ) => {
    if (Number(editingAlloc?.qty) < 0 || editingAlloc?.qty === "")
      return ErrorMessage("Quantity cannot be negative or empty");

    const qty = Number(editingAlloc?.qty);
    const isAbscond =
      isSelectionState && minBidQuantityMw && qty < Number(minBidQuantityMw);
    const status = isAbscond
      ? "ACCEPTED"
      : qty >= Number(minQty)
        ? "ACCEPTED"
        : "PENDING";

    const payload = {
      bidIdToQuantityMap: { [bidId]: qty },
      status,
    };
    const res = await updateQuantityRequest(payload);
    if (res?.data?.code === 200 && isAbscond) {
      await updateWinnerBidStatus({ bidIdToStatusMap: { [bidId]: "ABSCOND" } });
    }
    if (res?.data?.code === 200) {
      if (index === 0) {
        const reqBidIds = activeReq?.bids?.map((b) => b?.id) ?? [];

        let selBid = selectedBids.filter((id) => !reqBidIds.includes(id));
        let rejBid = rejectedBids.filter((id) => !reqBidIds.includes(id));
        setSelectedBids(selBid);
        setRejectedBids(rejBid);
        localStorage.setItem(
          lsKey,
          JSON.stringify({ selectedBids: selBid, rejectedBids: rejBid }),
        );
      } else {
        const selBid = selectedBids.filter((id) => id !== bidId);
        const rejBid = rejectedBids.filter((id) => id !== bidId);
        setSelectedBids(selBid);
        setRejectedBids(rejBid);
        localStorage.setItem(
          lsKey,
          JSON.stringify({ selectedBids: selBid, rejectedBids: rejBid }),
        );
      }
      SuccessMessage(res?.data?.message);
      closeEditAlloc();
      fetchReqList();
    }
  };

  const handleModalSubmit = () => {
    const pendingBids = allBids.filter(
      (bid) =>
        (bid.bidStatus === "SHORTLISTED_RA" ||
          (bid?.bidStatus === "SHORTLISTED" && bid?.bidStage === "CB")) &&
        bid.winnerBidStatus === "PENDING",
    );

    setConfirmation({
      status: true,
      header: "Confirm Allocation",
      data: "Once confirmed, documentation will be initiated for the selected bids.",
      note: `${pendingBids?.length > 0 ? `One or more bids are pending seller approval. If you proceed with documentation, all such pending bids will be rejected.` : ""}An initial obligation will be blocked as per terms and conditions, if applicable.`,
    });
  };

  const handleModalFinalSubmit = () => {
    handleFinalizeT_C();
  };

  const handleFinalizeT_C = async () => {
    dispatch(activeType2Loading(true));
    const payload = {
      selectedBidIds: selectedBids,
      tenderId: tenderData?.id,
      bidStage: "CB",
    };
    const res = await handleRaFinalizeT_C(payload);
    if (res?.data?.code === 200) {
      handleRemoveLocalStorage();
      SuccessMessage(res?.data?.message);
      localStorage.setItem("activetenderId", tenderData?.id);
      dispatch(activeTenderId(tenderData?.id));
      navigate(`/tender_active?id=${tenderData?.capacityId}`);

      dispatch(activeType2Loading(false));
    } else {
      dispatch(activeType2Loading(false));
      ErrorMessage(res?.data?.message || res?.data?.error);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const handleRejectAndClose = async () => {
    let res = await endTenderApi(tenderData?.id);
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      dispatch(auctionGet());
      handleClose();
    } else {
      ErrorMessage(res?.data?.message || res?.data?.error);
    }
  };

  const handleRemoveLocalStorage = () => {
    localStorage.removeItem(lsKey);
  };
  return (
    <>
      {updateQtyConfirmation?.status && (
        <ConfirmationModal
          open={updateQtyConfirmation.status}
          handleClose={() =>
            setUpdateQtyConfirmation({
              status: false,
              bidId: null,
              minQty: null,
              minBidQuantityMw: null,
              editingQty: null,
              index: null,
            })
          }
          header="Confirm Quantity Update"
          data="Are you sure you want to update the allocated quantity?"
          note={
            updateQtyConfirmation.minBidQuantityMw &&
            Number(updateQtyConfirmation.editingQty) <
              Number(updateQtyConfirmation.minBidQuantityMw)
              ? `Updating quantity below the minimum bid quantity (${updateQtyConfirmation.minBidQuantityMw} MW) will mark this bid as ABSCOND with the allotted quantity.`
              : ""
          }
          handleSubmit={() => {
            handleUpdateQuantityRequest(
              updateQtyConfirmation.bidId,
              updateQtyConfirmation.minQty,
              updateQtyConfirmation.index,
              updateQtyConfirmation.minBidQuantityMw,
            );
            setUpdateQtyConfirmation({
              status: false,
              bidId: null,
              minQty: null,
              minBidQuantityMw: null,
              editingQty: null,
              index: null,
            });
          }}
        />
      )}
      {rejectConfirmation?.status && (
        <ConfirmationModal
          open={rejectConfirmation?.status}
          handleClose={() =>
            setRejectConfirmation({
              status: false,
              header: "",
              data: "",
            })
          }
          data={rejectConfirmation?.data}
          header={rejectConfirmation?.header}
          handleSubmit={handleRejectAndClose}
          type={"TenderBid"}
        />
      )}
      {confirmation?.status && (
        <ConfirmationModal
          open={confirmation?.status}
          handleClose={() =>
            setConfirmation({
              status: false,
              header: "",
              data: "",
              note: "",
            })
          }
          data={confirmation?.data}
          header={confirmation?.header}
          note={confirmation?.note}
          handleSubmit={handleModalFinalSubmit}
          type={"TenderBid"}
        />
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={`popupModal ${Styles.viewSelectionContainer}`}>
          {/* Header */}
          <PdfPreviewModal />
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">View & Finalize Bid(s)</div>
            <Close
              style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>

          {/* Requisition List */}
          <div className={Styles.reqSelectionListContainer}>
            <div
              onClick={() => expanded !== 0 && onToggle(expanded - 1)}
              className={`${Styles.firstReqIndicator} ${expanded !== 0 ? Styles.activeStateButton : ""}`}
            >
              <KeyboardDoubleArrowLeftIcon style={{ fontSize: "14px" }} />
            </div>
            <div className={Styles.reqSelectionScrollArea} ref={scrollAreaRef}>
              {reqList?.map((ele, index) => (
                <div
                  className={`${Styles.reqSelectionText} ${expanded === index ? Styles.activeStateButton : ""}`}
                  key={ele?.id}
                  ref={(el) => (tabRefs.current[index] = el)}
                  onClick={() => onToggle(index)}
                >
                  <span>Requisition {index + 1}</span>
                  {ele?.bids?.filter(
                    (bid) => bid?.winnerBidStatus === "PENDING",
                  ).length > 0 && (
                    <span style={{ fontSize: "8px" }}>
                      {
                        ele?.bids?.filter(
                          (bid) => bid?.winnerBidStatus === "PENDING",
                        ).length
                      }{" "}
                      Pending
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div
              onClick={() =>
                expanded !== reqList?.length - 1 && onToggle(expanded + 1)
              }
              className={`${Styles.lastReqIndicator} ${expanded !== reqList?.length - 1 ? Styles.activeStateButton : ""}`}
            >
              <KeyboardDoubleArrowRightIcon style={{ fontSize: "14px" }} />
            </div>
          </div>

          {/* Requisition details */}

          <div className={Styles.reqSelfDetailsContainer}>
            <div className={Styles.reqDetailsRow}>
              <div>Date Range</div>
              <div>
                {formatDateType(activeReq?.dateStartAt)} -{" "}
                {formatDateType(activeReq?.dateEndAt)}
              </div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Time Range</div>
              <div>
                {activeReq?.slotStartAt} to{" "}
                {activeReq?.slotEndAt === "23:59"
                  ? "24:00"
                  : activeReq?.slotEndAt}
              </div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Required Qty</div>
              <div>{activeReq?.requiredQty} MW</div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Minimum Qty</div>
              <div>{activeReq?.minimumQty} MW</div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Lowest Bid Price</div>
              <div>₹{activeReq?.lowestBidPrice}/kWh</div>
            </div>
            <div className={Styles.reqDetailsRow}>
              <div>Bucket Filled</div>
              <div style={{ textAlign: "center" }}>
                <CircularProgress
                  value={getFilledQty(activeReq)}
                  size={40}
                  strokeWidth={4}
                  required={activeReq?.requiredQty}
                />
              </div>
            </div>
          </div>

          {/* Req. Bid Details */}

          <div className={Styles.reqDetailsRowHeading}>
            Requisition Bid List
          </div>

          <div className={Styles.bidDetailsContainer}>
            <div
              className={`display_flex_row tableHeading ${Styles.bidDetailsTable}`}
            >
              <div style={{ width: "15%" }}>Bid Price (₹/kWh)</div>
              <div style={{ width: "15%" }}>Bid Qty (MW)</div>
              <div style={{ width: "15%" }}>Min. Seller Quantity (MW)</div>
              <div style={{ width: "25%" }}>Allocated Qty (MW)</div>
              <div style={{ width: "15%" }}>Bank Guarantee</div>
              <div
                style={{
                  width: "20%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span>Status</span>
                {(selectedBids.length > 0 || rejectedBids.length > 0) && (
                  <span
                    onClick={handleClear}
                    className={`activeButton_type_2 ${Styles.clearSelectionBtn}`}
                  >
                    Clear Selection
                  </span>
                )}
              </div>
            </div>
            <div className={`scroll_bar_y ${Styles.bidDetailsScrollArea}`}>
              {(() => {
                const sortedBids = [...(activeReq?.bids ?? [])].sort(
                  (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
                );
                const isFirstBidWithdrawn =
                  sortedBids[0]?.winnerBidStatus === "WITHDRAWN";

                if (sortedBids.length === 0) {
                  return (
                    <div
                      className="display_flex_row"
                      style={{
                        justifyContent: "center",
                        padding: "16px 0",
                        color: "#888",
                      }}
                    >
                      No bids
                    </div>
                  );
                }
                return sortedBids.map((bid, index) => {
                  const isEditing = editingAlloc?.bidId === bid?.id;
                  const isAccepted = selectedBids.includes(bid?.id);
                  const isRejected = rejectedBids.includes(bid?.id);
                  const isPending = pendingBids?.some(
                    (pendingBid) => pendingBid.id === bid?.id,
                  );
                  const isWithdrawn = bid?.winnerBidStatus === "WITHDRAWN";
                  const isAbscond =
                    isSelectionState &&
                    bid?.selectedQuantity < Number(bid?.minBidQuantityMw);

                  return (
                    <div
                      key={bid?.id}
                      className="display_flex_row tableDetails"
                    >
                      <div style={{ width: "15%" }}>{bid?.bidPrice}</div>
                      <div style={{ width: "15%" }}>{bid?.bidQuantityMw}</div>
                      <div style={{ width: "15%" }}>
                        {bid?.minBidQuantityMw || "-"}
                      </div>
                      {/* Allocated Qty — edit only for this bid */}
                      <div style={{ width: "25%" }}>
                        {isEditing ? (
                          <div
                            className={Styles.allocatedInputMainCont}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="number"
                              value={editingAlloc.qty}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val !== "" && !/^\d*\.?\d{0,3}$/.test(val))
                                  return;
                                const currentQty = Number(
                                  bid?.selectedQuantity,
                                );

                                const minQty = activeReq?.minimumQty ?? 0;

                                const minBidQty = bid?.bidQuantityMw;

                                if (
                                  currentQty < minQty &&
                                  Number(val) > currentQty
                                )
                                  return ErrorMessage(
                                    `Cannot increase quantity as current allocated quantity is below minimum required quantity of ${bid?.selectedQuantity} MW`,
                                  );
                                if (Number(val) >= minBidQty) {
                                  return ErrorMessage(
                                    `Allocated quantity should be less than bid quantity.`,
                                  );
                                }
                                setEditingAlloc((prev) => ({
                                  ...prev,
                                  qty: val,
                                }));
                              }}
                            />
                            <div className={Styles.bidSelMain}>
                              <div onClick={closeEditAlloc}>
                                <ClearIcon style={{ fontSize: "10px" }} />
                              </div>
                              <div
                                onClick={() =>
                                  setUpdateQtyConfirmation({
                                    status: true,
                                    bidId: bid?.id,
                                    minQty: activeReq?.minimumQty,
                                    minBidQuantityMw: bid?.minBidQuantityMw,
                                    editingQty: editingAlloc?.qty,
                                    index,
                                  })
                                }
                              >
                                <DoneIcon style={{ fontSize: "10px" }} />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className={Styles.allocationMain}>
                            <span>
                              {bid?.requestedQuantity || bid?.selectedQuantity}
                            </span>
                            {isAbscond ? (
                              <Tooltip
                                title={
                                  "Rejected due to quantity below minimum bid quantity."
                                }
                              >
                                <IconButton>
                                  <PriorityHighIcon
                                    style={{
                                      cursor: "pointer",
                                      height: "16px",
                                      width: "16px",
                                      color: "red",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            ) : isRejected ? (
                              <></>
                            ) : isPending ? (
                              <Tooltip title={"Pending Approval from Seller"}>
                                <IconButton>
                                  <PriorityHighIcon
                                    style={{
                                      cursor: "pointer",
                                      height: "16px",
                                      width: "16px",
                                      color: "red",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <EditIcon
                                onClick={() =>
                                  !isCloseDatePassed && openEditAlloc(bid)
                                }
                                style={{
                                  fontSize: "16px",
                                  color: isCloseDatePassed
                                    ? "#cccccc"
                                    : "#34656D",
                                  cursor: isCloseDatePassed
                                    ? "not-allowed"
                                    : "pointer",
                                  pointerEvents: isCloseDatePassed
                                    ? "none"
                                    : "auto",
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Bank Guarantee */}
                      <div
                        style={{ width: "15%" }}
                        className={Styles.viewDocs}
                        onClick={() =>
                          bid.documentId &&
                          handlePreviewGeneralTerms(bid.documentId, {
                            title: "Bank Guarantee Document Preview",
                          })
                        }
                      >
                        {bid.documentId ? "View Doc" : "-"}
                      </div>

                      {/* Status */}
                      <div
                        style={{ width: "20%" }}
                        className={Styles.bidSectionMainContainer}
                      >
                        {isFirstBidWithdrawn || isWithdrawn ? (
                          <div className="rejected">Withdrawn</div>
                        ) : isAbscond ? (
                          <div className="rejected">Rejected</div>
                        ) : isPending ? (
                          <div className="pending">Pending</div>
                        ) : (
                          <>
                            <div
                              className={Styles.bidSelMain}
                              style={
                                isCloseDatePassed
                                  ? {
                                      opacity: 0.4,
                                      pointerEvents: "none",
                                      cursor: "not-allowed",
                                    }
                                  : {}
                              }
                            >
                              <div
                                className={isRejected ? Styles.rejectedBid : ""}
                                onClick={() =>
                                  !isCloseDatePassed &&
                                  handleRejectBid(bid?.id, index)
                                }
                              >
                                <ClearIcon style={{ fontSize: "10px" }} />
                              </div>
                              <div
                                className={
                                  isAccepted ? Styles.selectedBids : ""
                                }
                                onClick={() =>
                                  !isCloseDatePassed &&
                                  handleSelectBid(bid?.id, index)
                                }
                              >
                                <DoneIcon style={{ fontSize: "10px" }} />
                              </div>
                            </div>
                            <div
                              className={`${Styles.bidSelStatus} ${
                                isAccepted
                                  ? "accepted"
                                  : isRejected
                                    ? "rejected"
                                    : ""
                              }`}
                            >
                              {isAccepted
                                ? "Accepted"
                                : isRejected
                                  ? "Rejected"
                                  : "-"}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {isCloseDatePassed && (
            <div
              className={`${Styles.bidDetailsContainer} ${Styles.tenderHeader}`}
            >
              Note: The tender has ended, you cannot proceed further.
            </div>
          )}
          <div className={Styles.buttonMainContainer}>
            <div className="activeButton_type_3" onClick={handleClose}>
              Cancel
            </div>
            {allBidsRejected ? (
              <div
                onClick={() =>
                  !isCloseDatePassed &&
                  setRejectConfirmation({
                    status: true,
                    header: "Confirm Reject & Close",
                    data: "Are you sure you want to reject all bids and close the tender? This action cannot be undone.",
                  })
                }
                className={
                  isCloseDatePassed ? "activeButton_disable" : "activeButton"
                }
              >
                Reject All and Close Tender
              </div>
            ) : expanded !== reqList?.length - 1 ? (
              <div
                className={
                  isCloseDatePassed ? "activeButton_disable" : "activeButton"
                }
                onClick={() => !isCloseDatePassed && onToggle(expanded + 1)}
              >
                Save and Contine
              </div>
            ) : (
              <div
                onClick={() =>
                  !isCloseDatePassed && hasAnyAccepted && handleModalSubmit()
                }
                className={
                  isCloseDatePassed || !hasAnyAccepted
                    ? "activeButton_disable"
                    : "activeButton"
                }
              >
                Proceed to Documentation
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RFPSelectionModal;
